import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ethers } from "ethers";
import { motion, AnimatePresence } from "framer-motion";
import { useWallet } from "../hooks/useWallet";
import { useRPCProvider } from "../hooks/useRPCProvider";
import { CONTRACT_ADDRESSES, CONTRACT_ABIS } from "../constants";
import { useTranslation } from "../hooks/useTranslation";

function toArray(val) {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    if (val?.toArray) return val.toArray();
    return Array.from(val ?? []);
}

function parseOrder(o) {
    const user = o?.user ?? o?.[0];
    const poolId = o?.poolId ?? o?.[1];
    const tokenId = o?.tokenId ?? o?.[2];
    const nftContract = o?.nftContract ?? o?.[3];
    const openedAt = o?.openedAt ?? o?.[4];
    const boughtBack = o?.boughtBack ?? o?.[5];
    const poolOrderId = o?.poolOrderId ?? o?.[6];
    return {
        user: user?.toString?.() ?? String(user ?? ""),
        poolId: poolId != null ? (typeof poolId === "bigint" ? Number(poolId) : Number(poolId?.toString?.() ?? 0)) : 0,
        tokenId: tokenId?.toString?.() ?? String(tokenId ?? "0"),
        nftContract: nftContract?.toString?.() ?? String(nftContract ?? ""),
        openedAt: openedAt != null ? (typeof openedAt === "bigint" ? Number(openedAt) : Number(openedAt?.toString?.() ?? 0)) : 0,
        boughtBack: !!boughtBack,
        poolOrderId: poolOrderId?.toString?.() ?? String(poolOrderId ?? "0"),
    };
}

export default function History() {
    const navigate = useNavigate();
    const { account } = useWallet();
    const { executeWithFailover } = useRPCProvider();
    const { t } = useTranslation();

    const PER_PAGE = 8;
    const [showFilter, setShowFilter] = useState(false);
    const [timeRange, setTimeRange] = useState("All");
    const [purchaseHistory, setPurchaseHistory] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);

    const getTimeRangeBoundary = useCallback(() => {
        if (timeRange === "All") return 0;
        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime() / 1000;
        switch (timeRange) {
            case "Today": return startOfToday;
            case "1W": return startOfToday - 7 * 86400;
            case "1M": return startOfToday - 30 * 86400;
            case "3M": return startOfToday - 90 * 86400;
            default: return 0;
        }
    }, [timeRange]);

    const filteredHistory = useMemo(() => {
        const minTs = getTimeRangeBoundary();
        if (minTs === 0) return purchaseHistory;
        return purchaseHistory.filter((item) => item.timestamp >= minTs);
    }, [purchaseHistory, getTimeRangeBoundary]);

    const totalPages = Math.max(1, Math.ceil(filteredHistory.length / PER_PAGE));
    const paginatedHistory = useMemo(() => {
        const start = (currentPage - 1) * PER_PAGE;
        return filteredHistory.slice(start, start + PER_PAGE);
    }, [filteredHistory, currentPage]);

    useEffect(() => {
        setCurrentPage(1);
    }, [filteredHistory.length]);

    const fetchPurchaseHistory = useCallback(async () => {
        if (!account || !executeWithFailover) return;
        setIsLoading(true);
        let skipLoadingFalse = false;
        try {
            const history = await executeWithFailover(async (provider) => {
                const packsContract = new ethers.Contract(
                    CONTRACT_ADDRESSES.PACKS,
                    CONTRACT_ABIS.PACKS,
                    provider
                );
                const full = await packsContract.getUserAllOrders(account);
                console.log(full);
                const poolIds = full?.poolIds ?? full?.[0];
                const ordersByPool = full?.ordersByPool ?? full?.[1];
                const userReceivedBuyBackAmountsByPool = full?.userReceivedBuyBackAmountsByPool ?? full?.[4];
                const poolIdsArr = toArray(poolIds);
                const ordersArr = toArray(ordersByPool);
                const receivedArr = userReceivedBuyBackAmountsByPool
                    ? (Array.isArray(userReceivedBuyBackAmountsByPool) ? userReceivedBuyBackAmountsByPool : (userReceivedBuyBackAmountsByPool?.toArray?.() ?? Array.from(userReceivedBuyBackAmountsByPool ?? [])))
                    : [];
                const flatOrders = [];
                for (let i = 0; i < ordersArr.length; i++) {
                    const poolOrders = toArray(ordersArr[i]);
                    const pid = poolIdsArr[i] != null ? (typeof poolIdsArr[i] === "bigint" ? Number(poolIdsArr[i]) : Number(poolIdsArr[i])) : i;
                    const receivedForPool = toArray(receivedArr[i] ?? []);
                    for (let j = 0; j < poolOrders.length; j++) {
                        const o = poolOrders[j];
                        const parsed = parseOrder(o);
                        parsed.poolId = pid;
                        const receivedAmt = receivedForPool[j];
                        const receivedWei = receivedAmt != null ? (typeof receivedAmt === "bigint" ? receivedAmt.toString() : String(receivedAmt?.toString?.() ?? "0")) : "0";
                        parsed.userReceivedBuyBackWei = receivedWei;
                        flatOrders.push(parsed);
                    }
                }
                const uniquePoolIds = [...new Set(flatOrders.map((o) => o.poolId))];
                const poolCache = {};
                await Promise.all(
                    uniquePoolIds.map(async (poolId) => {
                        try {
                            const poolInfo = await packsContract.get(poolId);
                            const p = poolInfo?.price ?? poolInfo?.[2];
                            const priceStr = p != null ? (typeof p === "bigint" ? p.toString() : String(p?.toString?.() ?? "0")) : "0";
                            poolCache[poolId] = { price: priceStr };
                        } catch (_) {
                            poolCache[poolId] = { price: "0" };
                        }
                    })
                );
                const result = [];
                for (const ord of flatOrders) {
                    const pool = poolCache[ord.poolId] ?? { price: "0" };
                    const priceWei = pool.price;
                    const buyBackWei = ord.userReceivedBuyBackWei ?? "0";
                    const amountUsd = (parseFloat(priceWei) / 1e18).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                    const buyBackUsd = (parseFloat(buyBackWei) / 1e18).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                    result.push({
                        dateTime: ord.openedAt ? new Date(ord.openedAt * 1000).toLocaleString("en-GB") : "-",
                        amount: `${amountUsd} VUSDT`,
                        buyBackAmount: `${buyBackUsd} VUSDT`,
                        progress: ord.boughtBack ? "Sold" : "",
                        status: ord.boughtBack ? "buyback" : "complete",
                        timestamp: ord.openedAt,
                    });
                }
                result.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
                return result;
            });
            setPurchaseHistory(history || []);
        } catch (e) {
            if (e?.message?.includes("Wallet not detected")) {
                skipLoadingFalse = true;
                throw e;
            }
            if (e?.code !== "BAD_DATA" && e?.code !== "CALL_EXCEPTION") {
                console.warn("getUserAllOrders failed:", e);
            }
            setPurchaseHistory([]);
        } finally {
            if (!skipLoadingFalse) setIsLoading(false);
        }
    }, [account, executeWithFailover]);

    useEffect(() => {
        if (!account || !executeWithFailover) {
            setPurchaseHistory([]);
            return;
        }
        let retryCount = 0;
        const maxRetries = 5;
        const retryDelay = 400;
        const initialDelay = 150;

        const attemptFetch = () => {
            fetchPurchaseHistory().catch((e) => {
                if (e?.message?.includes("Wallet not detected") && retryCount < maxRetries) {
                    retryCount++;
                    setTimeout(attemptFetch, retryDelay);
                }
            });
        };
        const timer = setTimeout(attemptFetch, initialDelay);
        return () => clearTimeout(timer);
    }, [account, executeWithFailover, fetchPurchaseHistory]);

    const handleResetFilter = () => {
        setTimeRange("All");
    };

    const handleDoneFilter = () => {
        setShowFilter(false);
    };

    const renderHistoryTable = (historyData) => {
        return (
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b border-[#4d3168]">
                            <th className="text-left py-3 px-2 text-[#ad90cb] font-medium">
                                <div className="flex items-center gap-2">
                                    <span className="material-symbols-outlined text-base">schedule</span>
                                    {t("history.dateTime") || "Date & Time"}
                                </div>
                            </th>
                            <th className="text-left py-3 px-2 text-[#ad90cb] font-medium">
                                <div className="flex items-center gap-2">
                                    <span className="material-symbols-outlined text-base">arrow_upward</span>
                                    {t("history.packAmount") || "Pack Amount"}
                                </div>
                            </th>
                            <th className="text-left py-3 px-2 text-[#ad90cb] font-medium">
                                <div className="flex items-center gap-2">
                                    <span className="material-symbols-outlined text-base">arrow_upward</span>
                                    {t("history.buyBack") || "Buy Back"}
                                </div>
                            </th>
                            <th className="text-left py-3 px-2 text-[#ad90cb] font-medium">
                                <div className="flex items-center gap-2">
                                    <span className="material-symbols-outlined text-base">arrow_upward</span>
                                    {t("history.progress") || "Progress"}
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr>
                                <td colSpan={4} className="py-8 text-center text-[#ad90cb]">
                                    {t("common.loading") || "Loading..."}
                                </td>
                            </tr>
                        ) : historyData.length === 0 ? (
                            <tr>
                                <td colSpan={4} className="py-8 text-center text-[#ad90cb]">
                                    {t("history.noPurchaseHistory") || "No purchase history found"}
                                </td>
                            </tr>
                        ) : (
                            historyData.map((item, index) => (
                                <tr key={`${item.dateTime}-${index}`} className={`border-b ${index < historyData.length - 1 ? "border-[#4d3168]/50" : ""}`}>
                                    <td className="py-3 px-2 text-white text-xs">{item.dateTime}</td>
                                    <td className="py-3 px-2 text-white text-xs">{item.amount}</td>
                                    <td className="py-3 px-2 text-white text-xs">{item.status === "buyback" ? item.buyBackAmount : "—"}</td>
                                    <td className="py-3 px-2">
                                        {item.status === "buyback" ? (
                                            <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30">
                                                {t("history.sold") || "Sold"}
                                            </span>
                                        ) : (
                                            <a
                                                href="https://buypack.tcg.zone/profile"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30 transition-colors"
                                            >
                                                {t("history.buyBack") || "Buy Back"}
                                            </a>
                                        )}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <div className="relative overflow-x-hidden -mx-4 -my-8 min-h-[calc(100vh-200px)]">
            {/* Background gradient */}
            <div className="absolute inset-0 bg-gradient-to-b from-[#0a0610] via-[#241630] to-[#0a0610] -z-10"></div>

            {/* Subtle geometric pattern overlay */}
            <div
                className="absolute inset-0 opacity-[0.03] pointer-events-none -z-10"
                style={{
                    backgroundImage: `radial-gradient(circle at 2px 2px, #9770DD 1px, transparent 0)`,
                    backgroundSize: '40px 40px'
                }}
            ></div>

            {/* Additional pattern overlay */}
            <div
                className="absolute inset-0 opacity-[0.02] pointer-events-none -z-10"
                style={{
                    backgroundImage: `linear-gradient(45deg, transparent 48%, #9770DD 49%, #9770DD 51%, transparent 52%), linear-gradient(-45deg, transparent 48%, #9770DD 49%, #9770DD 51%, transparent 52%)`,
                    backgroundSize: '60px 60px'
                }}
            ></div>

            {/* Main Content */}
            <div className="relative z-10 pt-6 pb-6 px-4 max-w-md mx-auto space-y-6">
                {/* Header with back button */}
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center justify-center size-10 rounded-full text-white hover:bg-white/10 transition-colors"
                        aria-label={t("common.goBack")}
                    >
                        <span className="material-symbols-outlined text-2xl">
                            arrow_back
                        </span>
                    </button>
                    <h1 className="text-2xl font-semibold text-white">{t("general.history") || "History"}</h1>
                </div>

                {/* Purchase History Section */}
                <div className="rounded-3xl bg-[#1a1023]/70 backdrop-blur-md border border-[#9770DD]/30 p-5 shadow-lg shadow-[#9770DD]/15">
                    <div className="flex items-center justify-between mb-5">
                        <div className="flex items-center gap-3">
                            <span className="material-symbols-outlined text-gray-300 text-2xl" style={{ filter: 'drop-shadow(0 2px 4px rgba(255,255,255,0.3))' }}>
                                receipt_long
                            </span>
                            <h2 className="text-lg font-semibold text-white">
                                {t("history.purchaseHistory") || "Purchase History"}
                            </h2>
                        </div>
                        <button
                            onClick={() => setShowFilter(true)}
                            className="size-10 rounded-lg bg-[#241630]/60 border border-[#9770DD]/20 hover:bg-[#2d1b3d]/60 transition-colors flex items-center justify-center"
                            aria-label={t("common.filterLabel")}
                        >
                            <span className="material-symbols-outlined text-[#9770DD] text-lg">
                                filter_list
                            </span>
                        </button>
                    </div>
                    {renderHistoryTable(paginatedHistory)}
                    {filteredHistory.length > PER_PAGE && (
                        <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#9770DD]/20">
                            <button
                                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                disabled={currentPage <= 1}
                                className="px-4 py-2 rounded-lg bg-[#241630]/60 border border-[#9770DD]/20 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:border-[#9770DD]/40"
                            >
                                {t("common.prev") || "Prev"}
                            </button>
                            <span className="text-[#ad90cb] text-sm">
                                {t("history.page") || "Page"} {currentPage} {t("history.of") || "of"} {totalPages}
                            </span>
                            <button
                                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                                disabled={currentPage >= totalPages}
                                className="px-4 py-2 rounded-lg bg-[#241630]/60 border border-[#9770DD]/20 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:border-[#9770DD]/40"
                            >
                                {t("common.next") || "Next"}
                            </button>
                        </div>
                    )}
                </div>


            </div>

            {/* Filter Bottom Sheet Modal */}
            <AnimatePresence>
                {showFilter && (
                    <div className="fixed inset-0 z-[100] pointer-events-none">
                        {/* Backdrop */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setShowFilter(false)}
                            className="absolute inset-0 bg-gray-950/50 pointer-events-auto"
                        />

                        {/* Bottom Sheet */}
                        <div className="absolute inset-0 flex items-end justify-center pointer-events-none">
                            <motion.div
                                initial={{ y: "100%" }}
                                animate={{ y: 0 }}
                                exit={{ y: "100%" }}
                                transition={{ type: "spring", damping: 25, stiffness: 200 }}
                                className="w-full max-w-md bg-[#0a0610] rounded-t-3xl border-t border-[#9770DD]/30 shadow-2xl pointer-events-auto"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {/* Drag Handle */}
                                <div className="flex justify-center pt-3 pb-2">
                                    <div className="w-12 h-1 bg-gray-600 rounded-full"></div>
                                </div>

                                {/* Filter Content */}
                                <div className="px-5 pb-6 pt-2">
                                    {/* Title */}
                                    <h2 className="text-2xl font-semibold text-white mb-6">{t("history.filter") || t("common.filter") || "Filter"}</h2>

                                    {/* Time Range Section */}
                                    <div className="mb-6">
                                        <h3 className="text-white text-base font-medium mb-4">{t("history.timeRange") || "Time Range"}</h3>

                                        {/* Time Range Buttons */}
                                        <div className="grid grid-cols-3 gap-3 mb-4">
                                            {["All", "Today", "1W", "1M", "3M"].map((range) => (
                                                <button
                                                    key={range}
                                                    onClick={() => setTimeRange(range)}
                                                    className={`h-12 rounded-xl font-semibold text-sm transition-all ${timeRange === range
                                                        ? "bg-[#9770DD] text-white shadow-lg shadow-[#9770DD]/30"
                                                        : "bg-[#241630]/60 text-white border border-[#9770DD]/20 hover:border-[#9770DD]/40"
                                                        }`}
                                                >
                                                    {t(`history.time${range === "All" ? "All" : range === "Today" ? "Today" : range === "1W" ? "1W" : range === "1M" ? "1M" : "3M"}`) || range}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="flex gap-3 mt-8 mb-4">
                                        <button
                                            onClick={handleResetFilter}
                                            className="flex-1 h-14 rounded-2xl bg-[#241630]/60 border border-[#9770DD]/20 text-white font-bold text-lg hover:bg-[#2d1b3d]/60 hover:border-[#9770DD]/40 transition-all"
                                        >
                                            {t("history.reset") || t("common.reset") || "Reset"}
                                        </button>
                                        <button
                                            onClick={handleDoneFilter}
                                            className="flex-1 h-14 rounded-2xl bg-[#9770DD] text-white font-bold text-lg hover:bg-[#7a1fd6] transition-all shadow-lg shadow-[#9770DD]/30"
                                        >
                                            {t("history.done") || t("common.done") || "Done"}
                                        </button>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
}

